import React from 'react';

/**
 * Highlights matching text within a string by wrapping matches in <mark> tags
 * @param {string} text - The text to search within
 * @param {string} query - The search query to highlight
 * @param {string} className - Optional CSS class for the mark element
 * @returns {React.ReactNode} - Text with highlighted portions
 */
export function highlightText(text, query, className = 'search-highlight') {
    if (!text || !query || query.length < 2) {
        return text;
    }

    // Escape special regex characters in the query
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Create a case-insensitive regex to find matches
    const regex = new RegExp(`(${escapedQuery})`, 'gi');

    // Split the text by the query matches
    const parts = text.split(regex);

    if (parts.length === 1) {
        // No matches found
        return text;
    }

    // Map parts to React elements, highlighting matches
    return parts.map((part, index) => {
        if (part.toLowerCase() === query.toLowerCase()) {
            return (
                <mark key={index} className={className}>
                    {part}
                </mark>
            );
        }
        return part;
    });
}

/**
 * Truncates text and highlights matching portions
 * @param {string} text - The text to truncate and highlight
 * @param {string} query - The search query to highlight
 * @param {number} maxLength - Maximum length before truncation
 * @param {string} className - Optional CSS class for the mark element
 * @returns {React.ReactNode} - Truncated and highlighted text
 */
export function highlightAndTruncate(
    text,
    query,
    maxLength = 120,
    className = 'search-highlight'
) {
    if (!text) return '';

    // If there's a query, try to show the part with the match
    if (query && query.length >= 2) {
        const lowerText = text.toLowerCase();
        const lowerQuery = query.toLowerCase();
        const matchIndex = lowerText.indexOf(lowerQuery);

        if (matchIndex > -1 && text.length > maxLength) {
            // Calculate start position to show context around match
            const contextBefore = 40;
            let start = Math.max(0, matchIndex - contextBefore);
            let end = Math.min(text.length, start + maxLength);

            // Adjust start if we're near the end
            if (end === text.length) {
                start = Math.max(0, end - maxLength);
            }

            let truncated = text.slice(start, end);

            // Add ellipsis if truncated
            if (start > 0) truncated = '...' + truncated;
            if (end < text.length) truncated = truncated + '...';

            return highlightText(truncated, query, className);
        }
    }

    // Default truncation without match context
    const truncated =
        text.length > maxLength ? text.slice(0, maxLength) + '...' : text;

    return highlightText(truncated, query, className);
}

export default highlightText;
